import axiosInstance from './axios';

export interface Room {
  id: number;
  name: string;
  description: string;
  owner_id: number;
  owner_name?: string;
  price: string;
  price_type: 'monthly' | 'quarterly' | 'yearly' | 'one_time';
  service_fee_rate: string;
  trc20_address?: string;
  erc20_address?: string;
  qr_code?: string;
  max_members?: number;
  member_count?: number;
  is_active: number;
  is_hidden: number;
  room_creator_expires_at?: string;
  created_at: string;
  updated_at: string;
}

export interface RoomMessageUser {
  id: number;
  username: string;
  nickname?: string | null;
  avatar?: string | null;
  role: string;
}

export interface RoomMessage {
  id: number;
  room_id: number;
  user_id: number;
  user?: RoomMessageUser | null;
  content: string;
  image_url?: string | null;
  created_at: string;
  updated_at: string;
}

export interface RoomBan {
  id: number;
  room_id: number;
  user_id: number;
  reason?: string;
  expires_at?: string;
  created_at: string;
}

export interface RoomApiKey {
  id: number;
  room_id: number;
  name: string;
  api_key: string;
  api_secret: string;
  is_active: number;
  last_used_at?: string;
  created_at: string;
}

export interface RoomMembershipOrder {
  id: number;
  user_id: number;
  username: string;
  room_id: number;
  room_name: string;
  amount: number;
  price_type: string;
  status: string;
  txid?: string;
  from_address?: string;
  expired_at?: string;
  created_at: string;
  updated_at: string;
}

export const roomApi = {
  async getRooms(includeHidden?: boolean, search?: string) {
    const params: Record<string, string> = {};
    if (includeHidden) params.includeHidden = 'true';
    if (search) params.search = search;
    const response = await axiosInstance.get('/rooms', { params });
    return response.data;
  },

  async getRoom(roomId: number) {
    const response = await axiosInstance.get(`/rooms/${roomId}`);
    return response.data;
  },

  async getRoomMembers(roomId: number) {
    const response = await axiosInstance.get(`/rooms/members/${roomId}`);
    return response.data;
  },

  async checkMembership(roomId: number) {
    const response = await axiosInstance.get(`/rooms/check-membership/${roomId}`);
    return response.data;
  },

  async createRoom(data: {
    name: string;
    description?: string;
    price: number;
    price_type?: 'monthly' | 'yearly' | 'one_time';
    service_fee_rate?: number;
    trc20_address?: string;
    erc20_address?: string;
    qr_code?: string;
    max_members?: number;
  }) {
    const response = await axiosInstance.post('/rooms', data);
    return response.data;
  },

  async updateRoom(roomId: number, data: Partial<Room>) {
    const response = await axiosInstance.put(`/rooms/${roomId}`, data);
    return response.data;
  },

  async deleteRoom(roomId: number) {
    const response = await axiosInstance.delete(`/rooms/${roomId}`);
    return response.data;
  },

  async uploadQrCode(formData: FormData) {
    const response = await axiosInstance.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async addMember(roomId: number, userId: number) {
    const response = await axiosInstance.post(`/rooms/${roomId}/members`, { user_id: userId });
    return response.data;
  },

  async removeMember(roomId: number, userId: number) {
    const response = await axiosInstance.delete(`/rooms/${roomId}/members/${userId}`);
    return response.data;
  },

  async updateRoomStatus(roomId: number, status: string) {
    const response = await axiosInstance.put(`/admin/rooms/${roomId}/status`, { status });
    return response.data;
  },

  async renewRoom(roomId: number, duration: 'monthly' | 'quarterly' | 'yearly') {
    const response = await axiosInstance.post(`/admin/rooms/${roomId}/renew`, { duration });
    return response.data;
  },

  async searchUsers(keyword: string) {
    const response = await axiosInstance.get('/users/search', { params: { keyword } });
    return response.data;
  },

  async manualEnableMember(roomId: number, userId: number, membershipType: string, expiresAt?: string) {
    const response = await axiosInstance.post('/rooms/manual-enable', {
      room_id: roomId,
      user_id: userId,
      membership_type: membershipType,
      expires_at: expiresAt,
    });
    return response.data;
  },
};

export const roomMessageApi = {
  async sendMessage(roomId: number, content: string, imageData?: string | null) {
    const payload: { content: string; image_data?: string } = { content };
    if (imageData) {
      payload.image_data = imageData;
    }
    const response = await axiosInstance.post(`/rooms/${roomId}/messages`, payload);
    return response.data;
  },

  async getMessages(roomId: number, limit = 100, offset = 0) {
    const response = await axiosInstance.get(`/rooms/${roomId}/messages`, {
      params: { limit, offset },
    });
    return response.data;
  },

  async deleteMessage(messageId: number) {
    const response = await axiosInstance.delete(`/messages/${messageId}`);
    return response.data;
  },
};

export const roomPaymentApi = {
  async purchaseMembership(roomId: number, data: {
    payment_method?: string;
    transaction_id?: string;
  }) {
    const response = await axiosInstance.post(`/rooms/${roomId}/join`, data);
    return response.data;
  },

  async getPaymentHistory() {
    const response = await axiosInstance.get('/room-payments');
    return response.data;
  },

  async getRoomEarnings() {
    const response = await axiosInstance.get('/room-earnings');
    return response.data;
  },
};

export const withdrawApi = {
  async createWithdrawRequest(data: {
    amount: number;
    payment_info?: string;
    remark?: string;
  }) {
    const response = await axiosInstance.post('/withdraw', data);
    return response.data;
  },

  async getWithdrawRequests() {
    const response = await axiosInstance.get('/withdraw');
    return response.data;
  },

  async getAllWithdrawRequests(status?: string) {
    const response = await axiosInstance.get('/admin/withdraw', {
      params: status ? { status } : {},
    });
    return response.data;
  },

  async processWithdrawRequest(id: number, data: {
    status: 'approved' | 'paid' | 'rejected';
    admin_remark?: string;
  }) {
    const response = await axiosInstance.put(`/admin/withdraw/${id}`, data);
    return response.data;
  },
};

export const roomSettingsApi = {
  async banUser(roomId: number, data: { user_id: number; reason?: string; duration_hours?: number }) {
    const response = await axiosInstance.post(`/room-settings/${roomId}/bans`, data);
    return response.data;
  },

  async unbanUser(roomId: number, userId: number) {
    const response = await axiosInstance.delete(`/room-settings/${roomId}/bans/${userId}`);
    return response.data;
  },

  async getBannedUsers(roomId: number) {
    const response = await axiosInstance.get(`/room-settings/${roomId}/bans`);
    return response.data;
  },

  async checkBan(roomId: number) {
    const response = await axiosInstance.get(`/room-settings/${roomId}/check-ban`);
    return response.data;
  },

  async createApiKey(roomId: number, name?: string) {
    const response = await axiosInstance.post(`/room-settings/${roomId}/api-keys`, { name });
    return response.data;
  },

  async getApiKeys(roomId: number) {
    const response = await axiosInstance.get(`/room-settings/${roomId}/api-keys`);
    return response.data;
  },

  async deleteApiKey(roomId: number, keyId: number) {
    const response = await axiosInstance.delete(`/room-settings/${roomId}/api-keys/${keyId}`);
    return response.data;
  },

  async regenerateApiSecret(roomId: number, keyId: number) {
    const response = await axiosInstance.post(`/room-settings/${roomId}/api-keys/${keyId}/regenerate`);
    return response.data;
  },

  async toggleApiKeyStatus(roomId: number, keyId: number) {
    const response = await axiosInstance.post(`/room-settings/${roomId}/api-keys/${keyId}/toggle`);
    return response.data;
  },
};

export interface RoomCreationOrder {
  id: number;
  order_no: string;
  user_id: number;
  username: string;
  room_name: string;
  room_description?: string;
  amount_usdt: number;
  status: 'pending' | 'paid' | 'completed' | 'cancelled';
  transaction_hash?: string;
  created_at: string;
  updated_at: string;
}

export const roomCreationOrderApi = {
  async createOrder(data: { room_name: string; room_description?: string }) {
    const response = await axiosInstance.post('/rooms/orders', data);
    return response.data;
  },

  async getOrders() {
    const response = await axiosInstance.get('/rooms/orders');
    return response.data;
  },

  async getOrderDetail(orderId: number) {
    const response = await axiosInstance.get(`/rooms/orders/${orderId}`);
    return response.data;
  },

  async confirmPayment(data: { orderId: number; transactionHash: string }) {
    const response = await axiosInstance.post(`/rooms/orders/${data.orderId}/confirm`, {
      transactionHash: data.transactionHash,
    });
    return response.data;
  },
};

export const roomMembershipApi = {
  async getRoomMembershipOrders(roomId: number, status?: string) {
    const response = await axiosInstance.get('/room-membership/orders', {
      params: { room_id: roomId, status },
    });
    return response.data;
  },

  async getOwnerRoomMembershipOrders(roomId?: number, status?: string) {
    const response = await axiosInstance.get('/payment/owner/room-membership-orders', {
      params: roomId ? { room_id: roomId, status } : { status },
    });
    return response.data;
  },
};